import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api/client";
import { useCurrentUser } from "../contexts/CurrentUserContext";

// ─── Design tokens ───────────────────────────────────────────────────────────
const NAVY   = "#0C2D48";
const YELLOW = "#E4E69B";
const DANGER = "#E96A6A";

const PLATFORMS = [
  { id: "inperson",    label: "In Person",      icon: "🎙️", desc: "Aidey listens through your device microphone" },
  { id: "zoom",        label: "Zoom",            icon: "💙", desc: "Record your Zoom meeting, then paste the transcript" },
  { id: "teams",       label: "Microsoft Teams", icon: "💜", desc: "Use Teams live transcription, then paste below" },
  { id: "googlemeet",  label: "Google Meet",     icon: "🟢", desc: "Use Meet's captions/notes, then paste below" },
  { id: "other",       label: "Other / Paste",   icon: "📋", desc: "Paste any transcript or notes" },
];

// ─── Summary generator ───────────────────────────────────────────────────────
// Pattern-based summary from a transcript. Extracts decisions, topics, action items.
function generateSummary(transcript, allUsers) {
  const sentences = transcript
    .split(/[.!?\n]+/)
    .map(s => s.trim())
    .filter(s => s.length > 10);

  // Action item patterns
  const actionPatterns = [
    /\b(\w[\w\s]+?)\s+(?:will|is going to|needs? to|has to|should|must|is responsible for)\s+(.{8,80})/i,
    /\b(\w[\w\s]+?)\s+to\s+(follow up|send|review|update|check|create|write|prepare|contact|schedule|call|fix|build|share)\b.{0,60}/i,
    /^(?:action(?:\s+item)?|todo|task|follow.?up)[\s:]+(.+)/i,
  ];

  const decisionPatterns = [
    /(?:we|team|everyone)?\s*(?:agreed|decided|confirmed|resolved|approved|going with|will use|chose|selected)\s+(?:to\s+)?(.{8,100})/i,
    /decision[:–]\s*(.{8,100})/i,
  ];

  const actions   = [];
  const decisions = [];
  const topics    = new Set();

  // Extract keywords as topics from frequent nouns
  const wordFreq = {};
  const stopWords = new Set(["the","a","an","is","are","was","were","be","been","have","has","had","that","this","it","we","they","he","she","i","and","or","but","in","on","at","to","for","of","with","about","from","by"]);
  transcript.toLowerCase().match(/\b[a-z]{4,}\b/g)?.forEach(w => {
    if (!stopWords.has(w)) wordFreq[w] = (wordFreq[w] || 0) + 1;
  });
  Object.entries(wordFreq)
    .filter(([, v]) => v >= 2)
    .sort(([,a],[,b]) => b - a)
    .slice(0, 6)
    .forEach(([w]) => topics.add(w.charAt(0).toUpperCase() + w.slice(1)));

  for (const sentence of sentences) {
    let matched = false;
    for (const pat of actionPatterns) {
      const m = sentence.match(pat);
      if (m) {
        const nameHint = m[1]?.trim();
        const actionText = (m[2] || m[1] || sentence).trim();
        const user = allUsers.find(u =>
          nameHint && (u.name.toLowerCase().includes(nameHint.toLowerCase()) ||
          (u.department && u.department.toLowerCase().includes(nameHint.toLowerCase())) ||
          (u.jobTitle && u.jobTitle.toLowerCase().includes(nameHint.toLowerCase())))
        );
        actions.push({ text: actionText, assigneeName: user?.name || nameHint || null, assigneeId: user?.id || null, assigneeDept: null });
        matched = true;
        break;
      }
    }
    if (!matched) {
      for (const pat of decisionPatterns) {
        const m = sentence.match(pat);
        if (m) { decisions.push(m[1].trim()); break; }
      }
    }
  }

  return { actions: actions.slice(0, 20), decisions: decisions.slice(0, 10), topics: [...topics] };
}

// ─── Per-platform setup instructions (for non-developers) ────────────────────
const PLATFORM_GUIDES = {
  zoom: {
    steps: [
      { n: 1, text: "Open Zoom → Settings → Recording", action: { label: "Open Zoom Settings", url: "https://zoom.us/profile/setting" } },
      { n: 2, text: "Under Cloud Recording, enable 'Audio transcript'. This makes Zoom automatically create a text transcript after every recorded meeting." },
      { n: 3, text: "Start your meeting and click Record → Record to the Cloud." },
      { n: 4, text: "After the meeting ends, go to zoom.us → Recordings. Open the meeting and click the .vtt or text transcript file." },
      { n: 5, text: "Copy all the text and paste it into Aidey below." },
    ],
    tip: "Zoom AI Companion (paid plans) can do this automatically. OtterAI (free tier available) also integrates directly with Zoom for auto-transcription.",
  },
  teams: {
    steps: [
      { n: 1, text: "During the meeting, click the three-dot menu (…) → Start transcription. Everyone in the meeting will be notified." },
      { n: 2, text: "Teams will show a live transcript panel on the right side." },
      { n: 3, text: "After the meeting ends, go to the meeting chat or your Teams calendar and open the meeting." },
      { n: 4, text: "Click 'Transcript' → Download .docx or copy the text." },
      { n: 5, text: "Paste the transcript into Aidey below." },
    ],
    tip: "Teams transcription is available on Microsoft 365 Business Basic and above. The meeting organiser must enable it in meeting policies.",
  },
  googlemeet: {
    steps: [
      { n: 1, text: "During the meeting, click Activities (the puzzle-piece icon) → Transcripts → Start transcript." },
      { n: 2, text: "Or use Google Meet's AI notes: click the pencil icon to enable 'Take notes for me' (Workspace accounts)." },
      { n: 3, text: "After the meeting, the transcript is saved automatically to Google Drive as a Google Doc." },
      { n: 4, text: "Open the Doc → Select all (Ctrl/Cmd + A) → Copy → Paste into Aidey below." },
    ],
    tip: "Transcription in Google Meet requires a Google Workspace account (Business Starter or above). Personal Gmail accounts can use a Chrome extension like Otter.ai.",
  },
  other: {
    steps: [
      { n: 1, text: "Get your meeting transcript from whichever tool you use (Otter.ai, Fireflies.ai, your video platform, or your own notes)." },
      { n: 2, text: "Paste it into the text area below." },
    ],
    tip: "Otter.ai (otter.ai) has a free tier that records and transcribes meetings from Zoom, Teams, and Google Meet automatically.",
  },
};

// ─── Browser capability detection (Block 179) ────────────────────────────────
// Live recording uses the Web Speech API, which today is Chrome/Edge only. Detection is honestly
// imperfect: hasWebSpeech is a real feature check (reliable), but isPhone is UA-based and can be
// wrong (spoofed UAs, iPad reporting as desktop). We only use isPhone to choose which WARNING to
// show when recording IS available — it never gates functionality — so a wrong guess just shows a
// slightly less-tailored (still true) message.
const hasWebSpeech = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
const isPhone = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

// ─── Component ────────────────────────────────────────────────────────────────
export default function MeetingRecorder({ onClose, setFilter, onCreateTask }) {
  const { currentUser } = useCurrentUser();

  // Real workspace team members from the backend (not the legacy localStorage
  // userManager, which held stale mock users). The current user is always
  // included so they can be an attendee/assignee even if they're the team owner
  // (who has no teamMember row). Empty for solo users with no team — correct.
  const [allUsers, setAllUsers] = useState(
    currentUser ? [{ id: currentUser.id, name: currentUser.name || "You", email: currentUser.email, color: NAVY, department: null, jobTitle: null }] : []
  );
  useEffect(() => {
    const palette = ["#5352ed", "#e4a11b", "#22c55e", "#e96a6a", "#0c8599", "#7048e8", "#f76707", "#1098ad"];
    let cancelled = false;
    api("/api/team/members")
      .then(res => (res.ok ? res.json() : { members: [] }))
      .then(data => {
        if (cancelled) return;
        const members = (data.members || []).map((m, i) => ({
          id: m.id, name: m.name, email: m.email,
          color: palette[i % palette.length],
          department: m.teamRole || null, jobTitle: m.teamRole || null,
        }));
        if (currentUser && !members.find(u => u.id === currentUser.id)) {
          members.unshift({ id: currentUser.id, name: currentUser.name || "You", email: currentUser.email, color: NAVY, department: null, jobTitle: null });
        }
        if (members.length) setAllUsers(members);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [phase, setPhase]         = useState("setup");   // setup | record | review | summary | done
  const [platform, setPlatform]   = useState("inperson");
  const [title, setTitle]         = useState("");
  const [attendeeIds, setAttendeeIds] = useState(currentUser ? [currentUser.id] : []);
  const [transcript, setTranscript]   = useState("");
  const [pastedText, setPastedText]   = useState("");
  const [isListening, setIsListening] = useState(false);
  const [summary, setSummary]         = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [tasksDone, setTasksDone]     = useState(false);
  const [error, setError]             = useState("");
  const [showGuide, setShowGuide]     = useState(false);
  const [interimText, setInterimText] = useState("");
  const [externalInput, setExternalInput]         = useState("");
  const [externalAttendees, setExternalAttendees] = useState([]);
  const [isAnalysing, setIsAnalysing]             = useState(false);

  // Block 179: CRM contact this meeting is with. Default null ("no contact"), USER-PICKED only —
  // never inferred from the transcript. noteId is the Note created by process-transcript, which we
  // re-attach to the chosen contact at save.
  const [contacts, setContacts]     = useState([]);
  const [contactId, setContactId]   = useState(null);
  const [noteId, setNoteId]         = useState(null);

  const recognitionRef = useRef(null);
  const finalTranscript = useRef("");

  // Load CRM contacts for the picker.
  useEffect(() => {
    let cancelled = false;
    api("/api/contacts")
      .then(res => (res.ok ? res.json() : { contacts: [] }))
      .then(data => { if (!cancelled) setContacts(data.contacts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Web Speech API (in-person only) ────────────────────────────────────────
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("Live transcription needs Chrome or Edge. For other browsers, please use a video platform and paste the transcript.");
      return;
    }
    const rec = new SR();
    rec.continuous      = true;
    rec.interimResults  = true;
    rec.lang            = "en-GB";
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const text = e.results[i][0].transcript;
        if (e.results[i].isFinal) {
          finalTranscript.current += text + " ";
          setTranscript(finalTranscript.current);
        } else {
          interim = text;
        }
      }
      setInterimText(interim);
    };

    rec.onerror = (e) => {
      if (e.error !== "no-speech") setError(`Microphone error: ${e.error}. Check your browser permissions.`);
    };

    // Auto-restart after silence (recognition stops after ~15s of quiet)
    rec.onend = () => {
      if (isListening && recognitionRef.current === rec) {
        try { rec.start(); } catch {}
      }
    };

    rec.start();
    recognitionRef.current = rec;
    setIsListening(true);
  }, [isListening]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setInterimText("");
  }, []);

  useEffect(() => () => stopListening(), [stopListening]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleAttendee = (id) =>
    setAttendeeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const addExternalAttendee = () => {
    const name = externalInput.trim();
    if (!name) return;
    if (!externalAttendees.find(e => e.name.toLowerCase() === name.toLowerCase())) {
      setExternalAttendees(prev => [...prev, { name, isClient: true }]);
    }
    setExternalInput("");
  };
  const removeExternal = (name) =>
    setExternalAttendees(prev => prev.filter(e => e.name !== name));

  const attendees = attendeeIds.map(id => allUsers.find(u => u.id === id)).filter(Boolean);

  // Block 179: in-person uses live Web Speech text ONLY where it's available; everywhere else
  // (paste platforms, and in-person on a browser without Web Speech) the pasted text is the source.
  const canRecordLive = platform === "inperson" && hasWebSpeech;
  const fullTranscript = canRecordLive ? transcript : pastedText;

  // Block 179: contact picker (used at setup as an optional pre-select, and at the summary/save step
  // as the authoritative choice). "No contact" is the default. Never inferred from the transcript.
  const contactSelect = (label, hint) => (
    <div style={{ marginBottom: "1.5rem" }}>
      <label style={{ display: "block", fontWeight: 700, fontSize: "0.82rem", color: "#555", marginBottom: "0.4rem" }}>{label}</label>
      <select
        value={contactId || ""}
        onChange={e => setContactId(e.target.value || null)}
        style={{ width: "100%", padding: "0.6rem 0.9rem", border: "1px solid #ddd", borderRadius: 8, fontSize: "0.9rem", fontFamily: "Raleway, sans-serif", background: "#fff", boxSizing: "border-box" }}
      >
        <option value="">No contact</option>
        {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
      </select>
      {hint && <div style={{ marginTop: "0.35rem", fontSize: "0.75rem", color: "#bbb" }}>{hint}</div>}
    </div>
  );

  const handleGenerateSummary = async () => {
    const text = fullTranscript.trim();
    if (text.length < 30) { setError("Please add more content before generating a summary."); return; }
    setIsAnalysing(true);
    setError("");
    const allAttendeeNames = [
      ...attendees.map(u => u.name),
      ...externalAttendees.map(e => e.name),
    ];
    let result = null;
    // Block 179: ALL transcripts (live-recorded here, or pasted) go through process-transcript, the
    // single endpoint — same summary, decisions, action items WITH priority, topics, CRM matching,
    // and a saved Note. contactId carries any optional pre-selection; the picker at this step is the
    // authoritative choice, re-applied to the note at save.
    try {
      const res = await api("/api/meetings/google-meet/process-transcript", {
        method: "POST",
        body: JSON.stringify({
          transcriptText: text,
          meetingTitle: title || "Meeting",
          attendees: allAttendeeNames,
          contactId: contactId || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setNoteId(data.noteId || null);
        result = {
          actions: (data.actionItems || []).map(a => ({ text: a.task, assigneeName: a.assignee || null, priority: a.priority || "Medium" })),
          decisions: data.decisions || [],
          topics: data.topics || [],
        };
      } else if (res.status === 403) {
        setError("You've reached your AI usage limit. Upgrade to summarise more meetings.");
      }
    } catch {}
    // Fallback: if the endpoint failed (offline / limit), keep the feature working with the local
    // regex summariser. It has no priority/topics and creates no Note — a graceful degradation.
    if (!result) result = generateSummary(text, allUsers);
    const actionsWithIds = result.actions.map((a, i) => {
      const user = a.assigneeName
        ? allUsers.find(u => u.name.toLowerCase().includes(a.assigneeName.toLowerCase()))
        : null;
      return { ...a, id: i, keep: true, assigneeId: user?.id || null, assigneeName: a.assigneeName || null, priority: a.priority || "Medium" };
    });
    setSummary(result);
    setActionItems(actionsWithIds);
    setIsAnalysing(false);
    setPhase("summary");
  };

  const handleCreateTasks = async () => {
    const kept = actionItems.filter(a => a.keep);
    if (kept.length === 0) { setError("No action items selected."); return; }

    const today = new Date().toISOString().slice(0, 10);
    const meetingTitle = title || "Meeting";

    if (onCreateTask) {
      for (const item of kept) {
        const assignIds = item.assigneeIds?.length ? item.assigneeIds
          : item.assigneeId ? [item.assigneeId] : [];
        onCreateTask({
          title: item.text,
          date: today,
          priority: item.priority || "Medium",
          taskType: "Meeting",
          assignedTeam: assignIds,
          userId: assignIds[0] || null,
          details: `Action item from: ${meetingTitle}`,
        });
      }
    } else {
      // Persist to the workspace-scoped tasks API (was a localStorage orphan that
      // never reached the DB). onCreateTask is the normal path; this is the fallback.
      for (const item of kept) {
        const assignIds = item.assigneeIds?.length ? item.assigneeIds
          : item.assigneeId ? [item.assigneeId] : [];
        try {
          await api('/api/tasks', {
            method: 'POST',
            body: JSON.stringify({
              title: item.text,
              priority: item.priority || "Medium",
              date: today,
              datetime: new Date().toISOString(),
              taskType: "Meeting",
              userId: assignIds[0] || null,
              assignedTeam: assignIds,
              details: `Action item from: ${meetingTitle}`,
            }),
          });
        } catch (_) { /* skip a failed item, keep going */ }
      }
      window.dispatchEvent(new Event("aidey:tasksUpdated"));
    }

    // Block 179: attach the transcript Note (created by process-transcript) to the picked contact.
    // The user's choice wins — this PUT sets Note.contactId to exactly what they selected (or null
    // for "no contact"), overriding whatever was sent at analysis time.
    if (noteId) {
      try {
        await api(`/api/notes/${noteId}`, { method: "PUT", body: JSON.stringify({ contactId: contactId || null }) });
      } catch (_) { /* note stays with its analysis-time contact; non-fatal */ }
    }

    // Store meeting record
    const meetings = (() => { try { return JSON.parse(localStorage.getItem("meetingRecords") || "[]"); } catch { return []; } })();
    meetings.unshift({
      id: `mtg-${Date.now()}`,
      title: meetingTitle,
      platform,
      date: new Date().toISOString(),
      attendeeIds,
      externalAttendees,
      transcript: fullTranscript,
      summary,
      actionItems: kept,
    });
    localStorage.setItem("meetingRecords", JSON.stringify(meetings.slice(0, 50)));

    setTasksDone(true);
    setPhase("done");
  };

  const copySummary = () => {
    const att = attendees.map(u => u.name).join(", ");
    const acts = actionItems.filter(a => a.keep).map(a => `• ${a.assigneeName || "Unassigned"}: ${a.text}`).join("\n");
    const decs = summary?.decisions?.map(d => `• ${d}`).join("\n") || "";
    const text = [
      `Meeting Summary — ${title || "Meeting"}`,
      `Date: ${new Date().toLocaleDateString("en-GB")}`,
      `Attendees: ${att || "—"}`,
      "",
      summary?.decisions?.length ? `KEY DECISIONS\n${decs}` : "",
      acts ? `ACTION ITEMS\n${acts}` : "",
      summary?.topics?.length ? `TOPICS DISCUSSED\n${summary.topics.join(", ")}` : "",
    ].filter(Boolean).join("\n");
    navigator.clipboard?.writeText(text);
  };

  // ── Shared button style ─────────────────────────────────────────────────────
  const btn = (bg, color = "#242629", extra = {}) => ({
    padding: "0.65rem 1.4rem", border: "none", borderRadius: 8, fontWeight: 700,
    fontSize: "0.9rem", fontFamily: "Raleway, sans-serif", cursor: "pointer",
    background: bg, color, transition: "opacity 0.15s", ...extra,
  });

  // ── PHASE: SETUP ────────────────────────────────────────────────────────────
  if (phase === "setup") return (
    <div style={{ padding: "2rem", maxWidth: 700, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h2 style={{ margin: 0, color: NAVY, fontSize: "1.4rem" }}>🎙️ Start Meeting</h2>
          <p style={{ margin: "0.3rem 0 0", color: "#888", fontSize: "0.85rem" }}>Aidey will record and summarise your meeting, then create tasks for everyone.</p>
        </div>
        {onClose && <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#999" }}>✕</button>}
      </div>

      {error && <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 8, padding: "0.7rem 1rem", color: "#A12710", marginBottom: "1rem", fontSize: "0.85rem" }}>⚠ {error}</div>}

      {/* Meeting title */}
      <div style={{ marginBottom: "1.25rem" }}>
        <label style={{ display: "block", fontWeight: 700, fontSize: "0.82rem", color: "#555", marginBottom: "0.35rem" }}>Meeting title</label>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q2 Planning, Sprint Review, Client Call"
          style={{ width: "100%", padding: "0.7rem 0.9rem", border: "1px solid #ddd", borderRadius: 8, fontSize: "0.95rem", fontFamily: "Raleway, sans-serif", boxSizing: "border-box" }} />
      </div>

      {/* Platform picker */}
      <div style={{ marginBottom: "1.25rem" }}>
        <label style={{ display: "block", fontWeight: 700, fontSize: "0.82rem", color: "#555", marginBottom: "0.5rem" }}>Meeting type</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "0.5rem" }}>
          {PLATFORMS.map(p => (
            <button key={p.id} type="button" onClick={() => { setPlatform(p.id); setError(""); }} style={{
              padding: "0.75rem 0.85rem", border: `2px solid ${platform === p.id ? NAVY : "#ddd"}`,
              borderRadius: 10, background: platform === p.id ? `${NAVY}0d` : "#fafafa",
              cursor: "pointer", textAlign: "left", fontFamily: "Raleway, sans-serif",
            }}>
              <div style={{ fontSize: "1.2rem", marginBottom: "0.2rem" }}>{p.icon}</div>
              <div style={{ fontWeight: 700, fontSize: "0.85rem", color: NAVY }}>{p.label}</div>
              <div style={{ fontSize: "0.72rem", color: "#888", marginTop: "0.15rem", lineHeight: 1.3 }}>{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Platform setup guide */}
      {platform !== "inperson" && (
        <div style={{ marginBottom: "1.25rem" }}>
          <button type="button" onClick={() => setShowGuide(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: NAVY, fontWeight: 700, fontSize: "0.85rem", padding: 0, fontFamily: "Raleway, sans-serif" }}>
            {showGuide ? "▾" : "▸"} How to get your transcript from {PLATFORMS.find(p2 => p2.id === platform)?.label}
          </button>
          {showGuide && PLATFORM_GUIDES[platform] && (
            <div style={{ background: "#f0f5fb", border: "1px solid #d0e4f7", borderRadius: 10, padding: "1rem 1.25rem", marginTop: "0.5rem" }}>
              <ol style={{ margin: 0, padding: "0 0 0 1.2rem" }}>
                {PLATFORM_GUIDES[platform].steps.map(s => (
                  <li key={s.n} style={{ fontSize: "0.85rem", color: "#333", marginBottom: "0.5rem", lineHeight: 1.5 }}>
                    {s.text}
                    {s.action && (
                      <a href={s.action.url} target="_blank" rel="noopener noreferrer"
                        style={{ marginLeft: "0.5rem", color: NAVY, fontWeight: 700, fontSize: "0.8rem", textDecoration: "underline" }}>
                        {s.action.label} →
                      </a>
                    )}
                  </li>
                ))}
              </ol>
              {PLATFORM_GUIDES[platform].tip && (
                <div style={{ marginTop: "0.75rem", padding: "0.6rem 0.85rem", background: `${YELLOW}60`, borderRadius: 7, fontSize: "0.8rem", color: "#5a4a00" }}>
                  💡 <strong>Tip:</strong> {PLATFORM_GUIDES[platform].tip}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attendee picker */}
      <div style={{ marginBottom: "1.75rem" }}>
        <label style={{ display: "block", fontWeight: 700, fontSize: "0.82rem", color: "#555", marginBottom: "0.5rem" }}>Attendees</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {allUsers.map(u => {
            const on = attendeeIds.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggleAttendee(u.id)} style={{
                padding: "0.35rem 0.8rem", borderRadius: 20,
                border: `1.5px solid ${on ? u.color : "#e0e0e0"}`,
                background: on ? `${u.color}20` : "#f9f9f9",
                color: on ? u.color : "#888",
                fontWeight: 700, fontSize: "0.78rem", cursor: "pointer",
                fontFamily: "Raleway, sans-serif",
              }}>
                {u.name}
                {u.department ? ` · ${u.department}` : ""}
                {u.id === currentUser?.id ? " (you)" : ""}
              </button>
            );
          })}
        </div>
        {attendees.length > 0 && (
          <div style={{ marginTop: "0.5rem", fontSize: "0.78rem", color: "#888" }}>
            {attendees.length} attendee{attendees.length !== 1 ? "s" : ""} — Aidey will assign tasks to them from the meeting discussion
          </div>
        )}
      </div>

      {/* External / client attendees */}
      <div style={{ marginBottom: "1.75rem" }}>
        <label style={{ display: "block", fontWeight: 700, fontSize: "0.82rem", color: "#555", marginBottom: "0.4rem" }}>External attendees (clients / guests)</label>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <input
            value={externalInput}
            onChange={e => setExternalInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addExternalAttendee())}
            placeholder="Type name and press Enter"
            style={{ flex: 1, padding: "0.6rem 0.9rem", border: "1px solid #ddd", borderRadius: 8, fontSize: "0.88rem", fontFamily: "Raleway, sans-serif" }}
          />
          <button type="button" onClick={addExternalAttendee}
            style={{ padding: "0.6rem 1rem", background: NAVY, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", fontFamily: "Raleway, sans-serif" }}>
            Add
          </button>
        </div>
        {externalAttendees.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
            {externalAttendees.map(e => (
              <span key={e.name} style={{ padding: "0.3rem 0.7rem", background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 16, fontSize: "0.78rem", fontWeight: 700, color: "#e65100", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                {e.name} <span style={{ fontSize: "0.7rem", opacity: 0.7 }}>client</span>
                <button type="button" onClick={() => removeExternal(e.name)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#e65100", padding: 0, lineHeight: 1, fontSize: "0.9rem" }}>×</button>
              </span>
            ))}
          </div>
        )}
        <div style={{ marginTop: "0.4rem", fontSize: "0.75rem", color: "#bbb" }}>External attendees are shown in the summary but won't receive task assignments</div>
      </div>

      {/* Block 179: optional contact pre-select. You can also pick (or change) it at the summary step. */}
      {contactSelect("Who is this meeting with? (optional)", "Links the summary note to a CRM contact. You can also choose this after the meeting.")}

      <button onClick={() => { setError(""); setPhase("record"); }} style={btn(NAVY, "#fff", { width: "100%" })}>
        {canRecordLive ? "Start Recording →" : "Continue to Transcript →"}
      </button>
    </div>
  );

  // ── PHASE: RECORD ───────────────────────────────────────────────────────────
  if (phase === "record") return (
    <div style={{ padding: "2rem", maxWidth: 700, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <h2 style={{ margin: 0, color: NAVY, fontSize: "1.3rem" }}>
          {canRecordLive ? "🎙️ Recording…" : "📋 Paste Transcript"}
        </h2>
        <button onClick={() => setPhase("setup")} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontFamily: "Raleway, sans-serif", fontSize: "0.85rem" }}>← Back</button>
      </div>

      {error && <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 8, padding: "0.7rem 1rem", color: "#A12710", marginBottom: "1rem", fontSize: "0.85rem" }}>⚠ {error}</div>}

      {canRecordLive ? (
        <>
          {/* Block 179: phone warning — recording dies if the screen locks or the app is switched. */}
          {isPhone && (
            <div style={{ background: "#fff8e1", border: "1px solid #ffd54f", borderRadius: 8, padding: "0.7rem 1rem", color: "#7a5c00", marginBottom: "1rem", fontSize: "0.85rem" }}>
              Keep this screen open while you record. If your phone locks or you switch apps, recording will stop.
            </div>
          )}
          {/* Live recording UI */}
          <div style={{ background: isListening ? "#f0fff4" : "#f8f9fb", border: `2px solid ${isListening ? "#22c55e" : "#e0e0e0"}`, borderRadius: 12, padding: "1.25rem", marginBottom: "1rem", minHeight: 200, position: "relative" }}>
            {isListening && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#22c55e", animation: "pulse 1.2s infinite" }} />
                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#22c55e" }}>LISTENING</span>
              </div>
            )}
            <div style={{ fontSize: "0.9rem", color: "#333", lineHeight: 1.6, whiteSpace: "pre-wrap", minHeight: 120 }}>
              {transcript || <span style={{ color: "#bbb" }}>Your transcript will appear here as Aidey listens…</span>}
              {interimText && <span style={{ color: "#aaa" }}> {interimText}</span>}
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
            {!isListening ? (
              <button onClick={startListening} style={btn("#22c55e", "#fff")}>
                🎙️ Start Listening
              </button>
            ) : (
              <button onClick={stopListening} style={btn(DANGER, "#fff")}>
                ⏹ Pause
              </button>
            )}
            {transcript && (
              <button onClick={() => { stopListening(); setPhase("review"); }} style={btn(NAVY, "#fff")}>
                End Meeting & Review →
              </button>
            )}
          </div>

          <p style={{ fontSize: "0.78rem", color: "#aaa", marginTop: 0 }}>
            Works best in Chrome or Edge. Aidey listens to whoever is speaking near the device. For meetings on a video call, use the platform's own transcript feature instead.
          </p>
        </>
      ) : (
        <>
          {/* Block 179: in-person on a browser without Web Speech — recording isn't available; say so
              honestly and let them paste instead (the verbatim message points them here). */}
          {platform === "inperson" && !hasWebSpeech && (
            <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 8, padding: "0.7rem 1rem", color: "#A12710", marginBottom: "1rem", fontSize: "0.85rem" }}>
              Live recording is not supported in this browser yet. Use Chrome or Edge to record, or paste your transcript below.
            </div>
          )}
          {/* Paste transcript UI — the platform guide only renders for platforms that have one. */}
          {PLATFORM_GUIDES[platform] && (
            <div style={{ background: "#f0f5fb", border: "1px solid #d0e4f7", borderRadius: 10, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: "0.83rem", color: "#1d4ed8" }}>
              <strong>How to get your transcript:</strong>
              <ol style={{ margin: "0.4rem 0 0 1.2rem", padding: 0 }}>
                {PLATFORM_GUIDES[platform].steps.slice(0, 4).map(s => (
                  <li key={s.n} style={{ marginBottom: "0.25rem" }}>{s.text}
                    {s.action && <a href={s.action.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: "0.4rem", color: NAVY, fontWeight: 700 }}>{s.action.label} →</a>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <textarea
            value={pastedText}
            onChange={e => setPastedText(e.target.value)}
            placeholder={"Paste your meeting transcript here\u2026\n\nYou can also type your own meeting notes. Aidey will find action items like:\n  \u201cBen will update the report\u201d\n  \u201cThe marketing team needs to review the brief\u201d\n  \u201c- [ ] Send proposals \u2013 Sarah\u201d"}
            style={{ width: "100%", minHeight: 260, padding: "0.9rem", border: "1px solid #ddd", borderRadius: 10, fontSize: "0.9rem", fontFamily: "Raleway, sans-serif", boxSizing: "border-box", lineHeight: 1.6, resize: "vertical" }}
          />

          <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.75rem" }}>
            <button onClick={handleGenerateSummary} disabled={pastedText.trim().length < 30 || isAnalysing} style={btn(NAVY, "#fff", { opacity: pastedText.trim().length < 30 ? 0.5 : 1 })}>
              {isAnalysing ? "Analysing…" : "Generate Summary & Tasks →"}
            </button>
          </div>
        </>
      )}

      {/* Pulse animation for recording indicator */}
      <style>{`
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </div>
  );

  // ── PHASE: REVIEW (live only — review before summarising) ──────────────────
  if (phase === "review") return (
    <div style={{ padding: "2rem", maxWidth: 700, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <h2 style={{ margin: 0, color: NAVY }}>📝 Review Transcript</h2>
        <button onClick={() => setPhase("record")} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontFamily: "Raleway, sans-serif", fontSize: "0.85rem" }}>← Back</button>
      </div>
      <p style={{ color: "#888", fontSize: "0.85rem", marginBottom: "1rem" }}>Edit the transcript if anything was mis-heard, then generate your summary.</p>
      <textarea
        value={transcript}
        onChange={e => { setTranscript(e.target.value); finalTranscript.current = e.target.value; }}
        style={{ width: "100%", minHeight: 300, padding: "0.9rem", border: "1px solid #ddd", borderRadius: 10, fontSize: "0.88rem", fontFamily: "Raleway, sans-serif", boxSizing: "border-box", lineHeight: 1.6, resize: "vertical" }}
      />
      <button onClick={handleGenerateSummary} disabled={isAnalysing} style={{ ...btn(NAVY, "#fff"), marginTop: "0.75rem", width: "100%", opacity: isAnalysing ? 0.7 : 1 }}>
        {isAnalysing ? "Analysing…" : "Generate Summary & Tasks →"}
      </button>
    </div>
  );

  // ── PHASE: SUMMARY ──────────────────────────────────────────────────────────
  if (phase === "summary") return (
    <div style={{ padding: "2rem", maxWidth: 760, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
        <h2 style={{ margin: 0, color: NAVY }}>📋 Meeting Summary</h2>
        <button onClick={() => setPhase(platform === "inperson" ? "review" : "record")} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "0.85rem", fontFamily: "Raleway, sans-serif" }}>← Back</button>
      </div>

      {error && <div style={{ background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 8, padding: "0.7rem 1rem", color: "#A12710", marginBottom: "1rem", fontSize: "0.85rem" }}>⚠ {error}</div>}

      {/* Overview */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
        <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>Meeting</div>
        <div style={{ fontWeight: 900, fontSize: "1.15rem", color: NAVY }}>{title || "Untitled Meeting"}</div>
        <div style={{ fontSize: "0.82rem", color: "#888", marginTop: "0.25rem" }}>
          {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          {attendees.length > 0 && ` · ${attendees.map(u => u.name).join(", ")}`}
          {externalAttendees.length > 0 && (
            <span style={{ color: "#e65100" }}>{attendees.length > 0 ? ", " : " · "}{externalAttendees.map(e => e.name).join(", ")} <span style={{ fontSize: "0.72rem" }}>(client)</span></span>
          )}
        </div>
        {summary?.topics?.length > 0 && (
          <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {summary.topics.map(t => (
              <span key={t} style={{ fontSize: "0.72rem", background: `${NAVY}15`, color: NAVY, borderRadius: 12, padding: "0.2rem 0.6rem", fontWeight: 600 }}>{t}</span>
            ))}
          </div>
        )}
      </div>

      {/* Decisions */}
      {summary?.decisions?.length > 0 && (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.75rem" }}>Key Decisions</div>
          {summary.decisions.map((d, i) => (
            <div key={i} style={{ fontSize: "0.88rem", color: "#333", marginBottom: "0.4rem" }}>✓ {d}</div>
          ))}
        </div>
      )}

      {/* Action items — editable */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Action Items ({actionItems.filter(a => a.keep).length} selected)
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={() => setActionItems(a => a.map(i => ({ ...i, keep: true })))} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", color: NAVY, fontWeight: 700 }}>Select all</button>
            <button onClick={() => setActionItems(a => a.map(i => ({ ...i, keep: false })))} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", color: "#888", fontWeight: 700 }}>Clear all</button>
          </div>
        </div>

        {actionItems.length === 0 && (
          <div style={{ color: "#aaa", fontSize: "0.85rem", textAlign: "center", padding: "1rem 0" }}>No action items detected. Try editing the transcript to include phrases like "Ben will…" or "Marketing team to…"</div>
        )}

        {actionItems.map((item, i) => {
          const assignedUser = item.assigneeId ? allUsers.find(u => u.id === item.assigneeId) : null;
          return (
            <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: "0.65rem", padding: "0.65rem 0", borderBottom: i < actionItems.length - 1 ? "1px solid #f3f4f6" : "none" }}>
              <input type="checkbox" checked={item.keep} onChange={() => setActionItems(a => a.map(x => x.id === item.id ? { ...x, keep: !x.keep } : x))} style={{ marginTop: 4, cursor: "pointer", flexShrink: 0, width: 16, height: 16 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <input value={item.text} onChange={e => setActionItems(a => a.map(x => x.id === item.id ? { ...x, text: e.target.value } : x))}
                  style={{ width: "100%", border: "none", background: "transparent", fontSize: "0.88rem", fontFamily: "Raleway, sans-serif", color: item.keep ? "#333" : "#bbb", textDecoration: item.keep ? "none" : "line-through", outline: "none", padding: 0 }} />
              </div>
              <select
                value={item.assigneeId || ""}
                onChange={e => {
                  const uid = e.target.value || null; // user ids are UUIDs — do not parseInt
                  const user = uid ? allUsers.find(u => u.id === uid) : null;
                  setActionItems(a => a.map(x => x.id === item.id ? { ...x, assigneeId: uid, assigneeName: user?.name || null, assigneeIds: null } : x));
                }}
                style={{ fontSize: "0.75rem", border: "1px solid #e5e7eb", borderRadius: 6, padding: "0.2rem 0.4rem", fontFamily: "Raleway, sans-serif", background: assignedUser ? `${assignedUser.color}18` : "#f9fafb", color: assignedUser?.color || "#888", fontWeight: 700, flexShrink: 0 }}
              >
                <option value="">Unassigned</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}{u.jobTitle ? ` · ${u.jobTitle}` : ""}</option>)}
              </select>
            </div>
          );
        })}

        {/* Add action item manually */}
        <button onClick={() => setActionItems(a => [...a, { id: a.length, text: "", assigneeId: null, assigneeName: null, keep: true }])}
          style={{ marginTop: "0.65rem", background: "none", border: "none", cursor: "pointer", color: NAVY, fontWeight: 700, fontSize: "0.82rem", fontFamily: "Raleway, sans-serif", padding: "0.25rem 0" }}>
          + Add action item
        </button>
      </div>

      {/* Block 179: authoritative contact attribution — the user's pick here wins and is saved to the
          transcript note. Defaults to "No contact"; never inferred. */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1.25rem" }}>
        {contactSelect("Save this meeting to a contact", "The transcript note will be linked to this CRM contact. Leave as “No contact” to skip.")}
        <div style={{ margin: "-0.75rem 0 0", fontSize: "0.75rem", color: "#bbb" }}>Attribution is your choice — Aidey never guesses the contact from what was said.</div>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <button onClick={handleCreateTasks} style={btn(NAVY, "#fff")}>
          ✅ Create Tasks & Save Summary
        </button>
        <button onClick={copySummary} style={btn(YELLOW)}>
          📋 Copy Summary
        </button>
      </div>

      <p style={{ fontSize: "0.78rem", color: "#aaa", marginTop: "0.75rem" }}>Tasks will appear in each team member's task list immediately. The summary is saved to meeting history.</p>
    </div>
  );

  // ── PHASE: DONE ─────────────────────────────────────────────────────────────
  if (phase === "done") return (
    <div style={{ padding: "2rem", maxWidth: 640, margin: "0 auto", fontFamily: "Raleway, sans-serif", textAlign: "center" }}>
      <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>✅</div>
      <h2 style={{ color: NAVY, marginBottom: "0.5rem" }}>Meeting wrapped up!</h2>
      <p style={{ color: "#666", marginBottom: "1.75rem", fontSize: "0.9rem" }}>
        {actionItems.filter(a => a.keep).length} tasks created
        {attendees.length > 1 ? ` and shared with ${attendees.length} attendees` : ""}.
      </p>

      <div style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem", textAlign: "left" }}>
        <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.65rem" }}>Tasks created</div>
        {actionItems.filter(a => a.keep).map((a, i) => {
          const user = a.assigneeId ? allUsers.find(u => u.id === a.assigneeId) : null;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
              {user && <div style={{ width: 22, height: 22, borderRadius: "50%", background: user.color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.65rem", fontWeight: 700, flexShrink: 0 }}>{user.name.charAt(0)}</div>}
              <span style={{ fontSize: "0.85rem", color: "#333" }}><strong>{user?.name || "Unassigned"}:</strong> {a.text}</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
        <button onClick={copySummary} style={btn(YELLOW)}>📋 Copy Summary to Share</button>
        {setFilter && <button onClick={() => setFilter("Today")} style={btn(NAVY, "#fff")}>View in Tasks →</button>}
        {onClose && <button onClick={onClose} style={btn("#f3f4f6", "#555")}>Close</button>}
      </div>

      {/* KNOWN LIMITATION: meeting history (transcript + summary) is saved to this
          device's localStorage only — it is NOT synced to the backend, so it won't
          appear on other devices and is lost if the browser cache is cleared. The
          created TASKS are saved to the backend and are unaffected. A backend
          meeting-history table is a future block. */}
      <p style={{ fontSize: "0.72rem", color: "#bbb", marginTop: "1rem", maxWidth: 440, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
        Your tasks are saved to your account. The full meeting summary is kept on this device only for now — copy it above if you need it elsewhere.
      </p>
    </div>
  );

  return null;
}
